import type { Ref } from 'vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { fetchGetJobHistory } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface JobHistoryPageViewComposable {
  isFetchingViewData: Ref<boolean>;
  viewData: Ref<
    | {
        jobHistoryLogs?: openapi.JobHistoryContentOpenapi;
      }
    | null
    | undefined
  >;
}

export default function useJobHistoryPageView(
  queryJobId: Readonly<Ref<number | undefined>>,
  queryJobDate: Readonly<Ref<string | undefined>>,
): JobHistoryPageViewComposable {
  const route = useRoute();

  let jobHistoryLogsController: AbortController | undefined;

  const isFetchingViewData = ref(false);
  const fetchedJobHistoryLogsData = ref<openapi.JobHistoryContentOpenapi>();
  const viewData = ref<{
    jobHistoryLogs?: openapi.JobHistoryContentOpenapi;
  } | null>();

  function clear(): void {
    if (jobHistoryLogsController) jobHistoryLogsController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function fetchGetJobHistoryData(): Promise<void> {
    const jobId = queryJobId.value;
    const jobDate = queryJobDate.value;
    fetchedJobHistoryLogsData.value = undefined;
    if (jobHistoryLogsController) jobHistoryLogsController.abort();
    if (!jobId) return;
    if (!jobDate) return;
    jobHistoryLogsController = new AbortController();
    await fetchGetJobHistory(
      {
        job_id: jobId,
        job_date: jobDate,
      },
      {
        signal: jobHistoryLogsController.signal,
      },
    )
      .then((data) => {
        fetchedJobHistoryLogsData.value = data;
      })
      .catch((err: unknown) => {
        if (!isCancel(err)) throw err;
      });
  }

  function updateResults(): void {
    if (!fetchedJobHistoryLogsData.value) {
      viewData.value = null;
      return;
    }
    viewData.value = {
      jobHistoryLogs: fetchedJobHistoryLogsData.value,
    };
  }

  watch(
    [queryJobId, queryJobDate],
    async (newValues, oldValues) => {
      if (route.name === routeNames.JOB_HISTORY) {
        isFetchingViewData.value = true;
        try {
          await fetchGetJobHistoryData();
          updateResults();
        } finally {
          isFetchingViewData.value = false;
        }
      }
    },
    { immediate: true, deep: true },
  );

  return {
    isFetchingViewData,
    viewData,
  };
}
